import React, { useMemo } from 'react';
import { Share } from 'react-native';

import { useAppSelector } from '../../app/hooks';
import { IS_ANDROID } from '../../constants';
import { selectAppConfig } from '../../features/appconfig/store/selectors';
import { useIsEpisodeRead, useToggleEpisodeRead } from '../../hooks/useReadEpisodes';
import i18n from '../../localization/i18n';
import { PodcastEpisodeModel } from '../../models';
import {
  ContextualMenuListItem,
  ContextualMenuSeparator,
  ContextualMenuShell,
} from '../ContextualMenu';

interface EpisodeMenuProps {
  visible: boolean;
  onRequestClose: () => void;
  episode: PodcastEpisodeModel;
  handleViewPodcastPage: () => void;
}

export const EpisodeMenu: React.FC<EpisodeMenuProps> = ({
  visible,
  onRequestClose,
  episode,
  handleViewPodcastPage,
}) => {
  const appConfig = useAppSelector(selectAppConfig);

  const shareMenu = useMemo(() => {
    const message = episode.title + ' (' + episode.podcastTitle + ')';
    return (
      <ContextualMenuListItem
        item={{
          title: i18n.t('podcast.menu.share'),
          icon: 'share_player',
          onPress: () => {
            Share.share({
              message:
                i18n.t('share.podcast', {
                  podcast: message,
                  appName: appConfig.nomRadio,
                }) + (IS_ANDROID ? '\n' + episode.url : ''),
              url: episode.url,
            }).then(() => {
              onRequestClose();
            });
          },
        }}
      />
    );
  }, [appConfig, episode, onRequestClose]);

  const isRead = useIsEpisodeRead(episode.podcastId, episode.id);
  const toggleEpisodeRead = useToggleEpisodeRead(episode.podcastId, episode.id);

  return (
    <ContextualMenuShell visible={visible} onRequestClose={onRequestClose}>
      {shareMenu}
      <ContextualMenuSeparator />
      <ContextualMenuListItem
        item={{
          title: i18n.t('podcast.menu.viewPodcastPage'),
          icon: 'podcast',
          onPress: handleViewPodcastPage,
        }}
        onRequestClose={onRequestClose}
      />
      <ContextualMenuSeparator />
      <ContextualMenuListItem
        item={{
          title: isRead
            ? i18n.t('podcast.menu.markAsUnread')
            : i18n.t('podcast.menu.markAsRead'),
          icon: 'check',
          onPress: () => {
            toggleEpisodeRead();
          },
        }}
        onRequestClose={onRequestClose}
      />
    </ContextualMenuShell>
  );
};
